<template>
  <div class="wf-history">
    <el-card shadow="never" class="wf-card">
      <div slot="header" class="wf-head">
        <span class="wf-title">历史查询</span>
        <span class="wf-tip">按流程、状态、发起人、时间范围查询历史记录并导出；选择具体流程后自动展开该流程的表单字段列</span>
      </div>

      <el-tabs v-model="scope" class="wf-tabs" @tab-click="onScopeChange">
        <el-tab-pane v-for="s in scopes" :key="s.value" :label="s.label" :name="s.value" />
      </el-tabs>

      <div class="wf-filters">
        <el-select v-model="query.condition.flowKey" size="small" filterable clearable class="wf-sel-200"
                   placeholder="流程名称" @change="reload">
          <el-option v-for="f in flows" :key="f.flowKey" :label="flowOptionLabel(f)" :value="f.flowKey" />
        </el-select>

        <el-select v-model="query.condition.status" size="small" clearable class="wf-sel-130" placeholder="审批状态">
          <el-option v-for="o in statusOptions" :key="o.value" :label="o.label" :value="o.value" />
        </el-select>

        <el-input v-model="query.condition.initiatorName" size="small" clearable class="wf-inp-160"
                  placeholder="发起人" @keyup.enter.native="reload" />

        <el-date-picker v-model="timeRange" size="small" type="datetimerange" class="wf-date-360"
                        value-format="yyyy-MM-dd HH:mm:ss" range-separator="至"
                        start-placeholder="开始时间" end-placeholder="结束时间" @change="onTimeChange" />

        <el-button size="small" type="primary" icon="el-icon-search" @click="reload">查询</el-button>
        <el-button size="small" icon="el-icon-refresh-left" @click="reset">重置</el-button>
        <el-button size="small" type="success" plain icon="el-icon-download"
                   :loading="exporting" @click="doExport">{{ exporting ? '生成中...' : '导出' }}</el-button>
      </div>

      <el-table :data="rows" v-loading="loading" size="small" border class="wf-table">
        <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip fixed />
        <el-table-column label="流程名称" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.flowName || '已删除流程' }}</template>
        </el-table-column>
        <el-table-column prop="initiatorName" label="发起人" width="100" />
        <el-table-column label="审批状态" width="92" align="center">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusTagType(row.status)">{{ statusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="发起时间" width="158" />
        <el-table-column label="结束时间" width="158">
          <template slot-scope="{ row }">{{ row.endTime || '—' }}</template>
        </el-table-column>
        <el-table-column v-for="c in columns" :key="c.key" :label="c.label" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formValue(row, c.key) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="76" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openDetail(row)">查看</el-button>
          </template>
        </el-table-column>
        <template slot="empty">
          <div style="padding: 24px 0; color: #909399; font-size: 13px;">暂无历史记录，可切换分类或放宽筛选条件后重试</div>
        </template>
      </el-table>

      <el-pagination class="wf-pager" background layout="total, prev, pager, next"
                     :total="total" :current-page="query.page" :page-size="query.limit"
                     @current-change="onPage" />

      <el-dialog title="审批详情" :visible.sync="detailVisible" width="820px" top="6vh" append-to-body
                 :close-on-click-modal="false">
        <WflowDetail v-if="detailVisible" :instance-id="currentInstanceId" />
      </el-dialog>
    </el-card>
  </div>
</template>

<script>
import WflowDetail from './WflowDetail.vue'
import { historyPage, historyExport, historyScopes, historyFlows } from '@/api/wflow/history'
import { saveBlobAs, timestampFileName } from '@/utils/download'

export default {
  name: 'WflowHistory',
  components: { WflowDetail },
  data() {
    return {
      // 分类：我发起的(apply) / 我审批的(approve) / 抄送我的(cc) / 全部(all，仅管理员)
      scope: 'apply',
      scopes: [],
      statusOptions: [
        { label: '审批中', value: 0 },
        { label: '已通过', value: 1 },
        { label: '已拒绝', value: 2 },
        { label: '已撤销', value: 3 }
      ],
      flows: [],
      rows: [],
      // 动态表单列（仅选定单一流程时非空）
      columns: [],
      total: 0,
      loading: false,
      exporting: false,
      timeRange: null,
      query: {
        page: 1,
        limit: 10,
        condition: {
          scope: 'apply',
          flowKey: '',
          status: null,
          initiatorName: '',
          beginTime: null,
          endTime: null
        }
      },
      detailVisible: false,
      currentInstanceId: null
    }
  },
  created() {
    this.loadScopes()
    this.loadFlows()
    this.load()
  },
  methods: {
    // 可用分类（管理员额外含「全部」）
    loadScopes() {
      historyScopes().then(res => {
        if (res && res.code === 200 && res.data) {
          this.scopes = res.data.scopes || []
          if (this.scopes.length && !this.scopes.some(s => s.value === this.scope)) {
            this.scope = this.scopes[0].value
          }
          this.query.condition.scope = this.scope
          this.load()
        }
      })
    },
    // 流程下拉：当前用户数据范围内「历史上出现过实例」的流程（含已停用；已删除流程定义聚合为一项）
    loadFlows() {
      historyFlows().then(res => {
        if (res && res.code === 200) this.flows = res.data || []
      })
    },
    // 下拉文案：已删除流程定义聚合项统一显示为「已删除流程」，其余显示流程名
    flowOptionLabel(f) {
      return f.flowName || '已删除流程'
    },
    buildParams() {
      const c = this.query.condition
      return {
        page: this.query.page,
        limit: this.query.limit,
        condition: {
          scope: c.scope,
          flowKey: c.flowKey || null,
          status: c.status,
          initiatorName: c.initiatorName || null,
          beginTime: c.beginTime || null,
          endTime: c.endTime || null
        }
      }
    },
    load() {
      this.loading = true
      historyPage(this.buildParams()).then(res => {
        if (res && res.code === 200 && res.data) {
          const data = res.data
          this.columns = data.columns || []
          const page = data.page || {}
          this.rows = page.records || []
          this.total = page.total || 0
        }
      }).finally(() => {
        this.loading = false
      })
    },
    reload() {
      this.query.page = 1
      this.load()
    },
    onScopeChange() {
      this.query.condition.scope = this.scope
      this.reload()
    },
    onTimeChange(v) {
      const range = Array.isArray(v) ? v : []
      this.query.condition.beginTime = range[0] || null
      this.query.condition.endTime = range[1] || null
      this.reload()
    },
    onPage(p) {
      this.query.page = p
      this.load()
    },
    reset() {
      this.timeRange = null
      const c = this.query.condition
      c.flowKey = ''
      c.status = null
      c.initiatorName = ''
      c.beginTime = null
      c.endTime = null
      this.reload()
    },
    // 导出：按当前分类与筛选条件导出全部匹配记录
    doExport() {
      if (this.exporting) return
      if (!this.total) {
        this.$message.warning('当前查询条件下没有可导出的记录')
        return
      }
      this.exporting = true
      historyExport(this.buildParams()).then(res => {
        this.exporting = false
        return saveBlobAs(res, timestampFileName('审批历史查询'))
      }).catch(() => {
        this.exporting = false
        this.$message.error('导出失败，请稍后重试')
      })
    },
    openDetail(row) {
      this.currentInstanceId = row.instanceId
      this.detailVisible = true
    },
    formValue(row, key) {
      const text = row.formText ? row.formText[key] : ''
      return text === null || text === undefined || text === '' ? '—' : text
    },
    statusText(s) {
      return { 0: '审批中', 1: '已通过', 2: '已拒绝', 3: '已撤销' }[s] || '—'
    },
    statusTagType(s) {
      return { 0: 'warning', 1: 'success', 2: 'danger', 3: 'info' }[s] || 'info'
    }
  }
}
</script>

<style scoped>
.wf-history { padding: 4px; }
.wf-card >>> .el-card__body { padding: 16px 20px 20px; }
.wf-head { display: flex; align-items: baseline; gap: 12px; }
.wf-title { font-size: 16px; font-weight: 600; color: #303133; }
.wf-tip { font-size: 12px; color: #909399; }
.wf-tabs { margin-bottom: 4px; }
.wf-tabs >>> .el-tabs__header { margin-bottom: 12px; }
.wf-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 12px; }
.wf-sel-200 { width: 200px; }
.wf-sel-130 { width: 130px; }
.wf-inp-160 { width: 160px; }
.wf-date-360 { width: 360px; }
.wf-table { width: 100%; }
.wf-pager { margin-top: 12px; text-align: right; }
</style>
